import { z } from "zod";

import { getNormalEnum } from "../pseudoEnums.js";
import { zGuard } from "../zod/utils.js";

import { EffortLevel } from "./effortLevel.js";
import { ModelFeatures } from "./ModelFeatures.js";

/**
 * Effort tiers accepted by OpenAI-compatible reasoning APIs. Support is
 * model-specific; most OpenAI models top out at `xhigh`.
 */
export const OpenAiReasoningEffortLiteral = z.enum([
  "none",
  "minimal",
  "low",
  "medium",
  "high",
  "xhigh",
  "max",
]);
export type OpenAiReasoningEffort = z.infer<
  typeof OpenAiReasoningEffortLiteral
>;

export const OpenAiFastServiceTierLiteral = z.enum(["fast", "priority"]);
export type OpenAiFastServiceTier = z.infer<
  typeof OpenAiFastServiceTierLiteral
>;

/** Canonical {@link EffortLevel} → OpenAI API param; `max` collapses to `xhigh`. */
export function toOpenAi(p: EffortLevel): OpenAiReasoningEffort {
  return p === "max" ? "xhigh" : p;
}

/** @deprecated */
export const DeprecatedOpenAiChatModelLiteral = z.enum([
  "ULTIMA_ALPHA",
  "GPT_4_OMNI",
  "GPT_4_OMNI_MINI",
  "GPT_41",
  "GPT_5",
  "GPT_51",
  "GPT_53_CODEX",
  "GPT_NEXT_V2_AUG_2026",
  "O3",
  "GPT_41_MINI",
  "GPT_54_NANO",
  "GPT_54",
  "GPT_54_MINI",
  "GPT_55",
  "GPT_41_NANO",
  "GPT_52",
  "CREST_ALPHA",
  "JOULE_ALPHA",
  "FIREFLY_ALPHA",
]);
/** @deprecated */
export type DeprecatedOpenAiChatModel = z.infer<
  typeof DeprecatedOpenAiChatModelLiteral
>;
/** @deprecated */
export const DeprecatedOpenAiChatModel = getNormalEnum(
  DeprecatedOpenAiChatModelLiteral,
);

export const OpenAiChatModelLiteral = z.enum([
  "GPT_56",
  "GPT_56_TERRA",
  "GPT_56_LUNA",
  "GPT_6_ASTRA",
  "GPT_6_SOL",
  "GPT_61_SOL",
  "GPT_6_LUNA",
]);
export type OpenAiChatModel = z.infer<typeof OpenAiChatModelLiteral>;
export const OpenAiChatModel = getNormalEnum(OpenAiChatModelLiteral);

type OpenAiChatModelFeatures = ModelFeatures & {
  // OpenAI documentation of how they count tokens seems to be kinda off, so
  // for now we'll just subtract 10 from the max tokens to be safe.
  maxInputTokens: number;
  maxOutputTokens: number;
  tokensPerMessage: number;
  /** Accepts `none` reasoning. */
  noneEffort?: true;
  /** Accepts `minimal` reasoning (OpenAI added this in 5.1). */
  minimalEffort?: true;
  /** Accepts `xhigh` reasoning. */
  xHighEffort?: true;
  /** Accepts `max` reasoning. */
  maxEffort?: true;
};

export const OpenAiChatModelFeatureMap: Record<
  OpenAiChatModel,
  OpenAiChatModelFeatures
> = {
  [OpenAiChatModel.GPT_56]: {
    name: "gpt-5.6",
    label: "GPT 5.6 Sol",
    requiresDataRetention: false,
    maxInputTokens: 1050000 - 128000 - 10,
    maxOutputTokens: 128000,
    tokensPerMessage: 3,
    noneEffort: true,
    xHighEffort: true,
    maxEffort: true,
  },
  [OpenAiChatModel.GPT_56_TERRA]: {
    name: "gpt-5.6-terra",
    label: "GPT 5.6 Terra",
    requiresDataRetention: false,
    maxInputTokens: 1050000 - 128000 - 10,
    maxOutputTokens: 128000,
    tokensPerMessage: 3,
    noneEffort: true,
    xHighEffort: true,
    maxEffort: true,
  },
  [OpenAiChatModel.GPT_56_LUNA]: {
    name: "gpt-5.6-luna",
    label: "GPT 5.6 Luna",
    requiresDataRetention: false,
    maxInputTokens: 1050000 - 128000 - 10,
    maxOutputTokens: 128000,
    tokensPerMessage: 3,
    noneEffort: true,
    xHighEffort: true,
    maxEffort: true,
  },
  [OpenAiChatModel.GPT_6_ASTRA]: {
    name: "gpt-6-astra",
    label: "GPT 6 Astra",
    requiresDataRetention: false,
    maxInputTokens: 1050000 - 128000 - 10,
    maxOutputTokens: 128000,
    tokensPerMessage: 3,
    xHighEffort: true,
    maxEffort: true,
  },
  [OpenAiChatModel.GPT_6_SOL]: {
    name: "gpt-6-sol",
    label: "GPT 6 Sol",
    requiresDataRetention: false,
    maxInputTokens: 1050000 - 128000 - 10,
    maxOutputTokens: 128000,
    tokensPerMessage: 3,
    noneEffort: true,
    xHighEffort: true,
    maxEffort: true,
  },
  [OpenAiChatModel.GPT_61_SOL]: {
    name: "gpt-6.1-sol",
    label: "GPT 6.1 Sol",
    requiresDataRetention: false,
    maxInputTokens: 1050000 - 128000 - 10,
    maxOutputTokens: 128000,
    tokensPerMessage: 3,
    xHighEffort: true,
    maxEffort: true,
  },
  [OpenAiChatModel.GPT_6_LUNA]: {
    name: "gpt-6-luna",
    label: "GPT 6 Luna",
    requiresDataRetention: false,
    maxInputTokens: 1050000 - 128000 - 10,
    maxOutputTokens: 128000,
    tokensPerMessage: 3,
    noneEffort: true,
    xHighEffort: true,
  },
};

/**
 * Assistant phase metadata from the OpenAI Responses API.
 */
export const OpenAIAssistantPhaseLiteral = z.enum([
  "commentary",
  "final_answer",
]);
export type OpenAIAssistantPhase = z.infer<typeof OpenAIAssistantPhaseLiteral>;
export const isOpenAIAssistantPhase = (
  phase: unknown,
): phase is OpenAIAssistantPhase =>
  phase === "commentary" || phase === "final_answer";

// These params are not allowed to be set for reasoning models
// The API will return a 400 if they are
export const openAiReasoningModelChatCompletionParams = {
  temperature: undefined,
  top_p: undefined,
  parallel_tool_calls: undefined,
};

export const OpenAiModelParams = z.object({
  frequencyPenalty: z.number(),
  n: z.number(),
  presencePenalty: z.number(),
  stopWords: z.array(z.string()),
  temperature: z.number().optional(), // Not used by reasoning models
  topP: z.number().optional(), // Not used by reasoning models
  reasoningEffort: OpenAiReasoningEffortLiteral.optional(), // ONLY used by reasoning models
  serviceTier: OpenAiFastServiceTierLiteral.optional(),
  verbosity: z.enum(["low", "medium", "high"]).optional(),
  model: OpenAiChatModelLiteral,
  toolSearch: z
    .object({
      enabled: z.boolean(),
      /** Tool names that should never be deferred (always loaded into context) */
      alwaysLoadedTools: z.array(z.string()).optional(),
    })
    .optional(),
});
export type OpenAiModelParams = z.infer<typeof OpenAiModelParams>;

/**
 * OpenAI chat models that support Fast mode (`serviceTier: "fast" |
 * "priority"`). Adding a model here is the single allowlist; billable-model
 * mapping in `getBillableModel` is typed against this list so new Fast models
 * require a corresponding billable-model mapping.
 */
export const OPENAI_FAST_MODE_CHAT_MODELS = [
  OpenAiChatModel.GPT_56,
  OpenAiChatModel.GPT_6_LUNA,
  OpenAiChatModel.GPT_6_ASTRA,
  OpenAiChatModel.GPT_6_SOL,
  OpenAiChatModel.GPT_61_SOL,
] as const satisfies readonly OpenAiChatModel[];

export type OpenAiFastModeChatModel =
  (typeof OPENAI_FAST_MODE_CHAT_MODELS)[number];

export const isOpenAiFastModeChatModel = (
  model: OpenAiModelParams["model"],
): model is OpenAiFastModeChatModel =>
  OPENAI_FAST_MODE_CHAT_MODELS.some((fastModel) => fastModel === model);

export const isOpenAiFastModeParams = (
  modelParams: OpenAiModelParams,
): modelParams is Omit<OpenAiModelParams, "model"> & {
  model: OpenAiFastModeChatModel;
} =>
  isOpenAiFastModeChatModel(modelParams.model) &&
  zGuard(OpenAiFastServiceTierLiteral, modelParams.serviceTier);

/**
 * Maps the requested OpenAI effort tier to the value to send, clamping tiers
 * the target model doesn't support down to the next-best supported one.
 */
export function buildOpenAiEffortForRequest(
  modelParams: OpenAiModelParams,
): OpenAiReasoningEffort | null {
  const effort = modelParams.reasoningEffort ?? null;
  if (effort == null) return null;
  const features = OpenAiChatModelFeatureMap[modelParams.model];
  if (effort === "none" && !features.noneEffort) return "low";
  if (effort === "minimal" && !features.minimalEffort) return "low";
  if (effort === "xhigh" && !features.xHighEffort) return "high";
  if (effort === "max" && !features.maxEffort) {
    return features.xHighEffort ? "xhigh" : "high";
  }
  return effort;
}

export const OpenAiToolSearchConfig = z.object({
  enabled: z.boolean(),
  /** Tool names that should never be deferred (always loaded into context) */
  alwaysLoadedTools: z.array(z.string()).optional(),
});
export type OpenAiToolSearchConfig = z.infer<typeof OpenAiToolSearchConfig>;

export const OpenAiCompactionConfig = z.object({
  enabled: z.boolean(),
  compactThreshold: z.number().optional(),
});
export type OpenAiCompactionConfig = z.infer<typeof OpenAiCompactionConfig>;

export const OpenAiDefaultCompactionConfig: OpenAiCompactionConfig = {
  enabled: true,
  compactThreshold: 300000,
};

// Based on `ChatCompletionRequestMessageRoleEnum` from openai package
export const OpenAiChatMessageRoleLiteral = z.enum([
  "system",
  "user",
  "assistant",
  "tool",
]);
export type OpenAiChatMessageRole = z.infer<
  typeof OpenAiChatMessageRoleLiteral
>;
export const OpenAiChatMessageRole = getNormalEnum(
  OpenAiChatMessageRoleLiteral,
);

// based on `ChatCompletionContentPartImage` from openai package
const OpenAiChatContentPartImage = z.object({
  type: z.literal("image_url"),
  image_url: z.object({
    url: z.string(),
  }),
});

// based on `ChatCompletionContentPartFile` from openai package
// See https://developers.openai.com/api/docs/guides/pdf-files/
const OpenAiChatContentPartFile = z.object({
  type: z.literal("file"),
  file: z.object({
    filename: z.string(),
    file_data: z.string(), // base64-encoded file with data URI prefix
  }),
});

const OpenAiChatContentPart = z.union([
  OpenAiChatContentPartImage,
  OpenAiChatContentPartFile,
]);

// Based on `ChatCompletionRequestMessage` from openai package
export const OpenAiChatMessage = z.union([
  z.object({
    content: z.string(),
    role: z.literal(OpenAiChatMessageRole.system),
  }),
  z.object({
    content: z.string().or(z.array(OpenAiChatContentPart)),
    role: z.literal(OpenAiChatMessageRole.user),
  }),
  z.object({
    content: z.string().or(z.null()),
    // OpenAI requires snake case
    function_call: z
      .object({ arguments: z.string(), name: z.string() })
      .optional(),
    reasoning_content: z.string().optional(),
    role: z.literal(OpenAiChatMessageRole.assistant),
  }),
  z.object({
    content: z.string(),
    role: z.literal(OpenAiChatMessageRole.tool),
    tool_call_id: z.string(),
  }),
  z.object({
    content: z.string().or(z.null()).optional(),
    reasoning_content: z.string().optional(),
    tool_calls: z.array(
      z.object({
        type: z.literal("function"),
        id: z.string(),
        function: z.object({ name: z.string(), arguments: z.string() }),
      }),
    ),
    role: z.literal(OpenAiChatMessageRole.assistant),
  }),
]);
export type OpenAiChatMessage = z.infer<typeof OpenAiChatMessage>;

export const OpenAIConformerResult = z.object({
  messages: z.array(OpenAiChatMessage),
});
export type OpenAIConformerResult = z.infer<typeof OpenAIConformerResult>;
